'use client';

import { useMemo } from 'react';
import {
  Badge,
  MetaDefault,
  MonoSmall,
  ReadOnlyCodeEditor,
  Tabs,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import type { AgentTraceIO, AgentTraceNode } from './buildAgentTraceTree';
import { AgentMessageReadableContent } from './AgentMessageReadableContent';
import { AgentToolCallCards } from './AgentToolCallCards';
import { formatToolCallArgs } from './agentMessageViewUtils';

const KIND_LABEL: Record<AgentTraceNode['kind'], string> = {
  chain: 'Chain',
  system: 'System',
  human: 'Human',
  llm: 'LLM',
  tool: 'Tool',
  subagent: 'Sub-agent',
};

function StatusBadge({ status }: { status?: string }) {
  if (!status) {
    return null;
  }

  const color =
    status === 'success'
      ? 'var(--colors-green-9)'
      : status === 'error'
        ? 'var(--colors-red-9)'
        : 'var(--colors-fg-muted)';

  return (
    <Badge variant="outline" size="sm" color={color} borderColor={color}>
      {status}
    </Badge>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <MetaDefault color="fg.subtle" fontStyle="italic">
      {label}
    </MetaDefault>
  );
}

function IoContent({ io }: { io?: AgentTraceIO }) {
  if (!io) {
    return <EmptyState label="Nothing captured" />;
  }

  if (io.type === 'text') {
    if (!io.text.trim()) {
      return <EmptyState label="Empty" />;
    }

    return <AgentMessageReadableContent text={io.text} />;
  }

  if (io.type === 'json') {
    return (
      <Box borderRadius="md" overflow="hidden">
        <ReadOnlyCodeEditor
          value={formatToolCallArgs(io.value) || 'null'}
          language="json"
          showLineNumbers
          maxHeight="360px"
        />
      </Box>
    );
  }

  return (
    <VStack alignItems="stretch" gap={3}>
      {io.text.trim() ? (
        <AgentMessageReadableContent text={io.text} />
      ) : (
        <EmptyState label="No assistant text" />
      )}
      {io.toolCalls.length > 0 && (
        <VStack alignItems="stretch" gap={1.5}>
          <MetaDefault
            color="fg.muted"
            fontWeight="semibold"
            textTransform="uppercase"
          >
            Tool calls
          </MetaDefault>
          <AgentToolCallCards toolCalls={io.toolCalls} />
        </VStack>
      )}
    </VStack>
  );
}

function tabTriggerProps(value: string) {
  return {
    value,
    fontSize: 'xs' as const,
    fontWeight: 'medium' as const,
    px: 3,
    py: 2,
  };
}

export function AgentTraceDetail({ node }: { node: AgentTraceNode | null }) {
  const rawJson = useMemo(
    () =>
      node
        ? JSON.stringify(
            {
              id: node.id,
              kind: node.kind,
              name: node.name,
              status: node.status,
              metadata: node.metadata,
              input: node.input,
              output: node.output,
            },
            null,
            2,
          )
        : '',
    [node],
  );

  const metadataJson = useMemo(
    () => (node ? JSON.stringify(node.metadata, null, 2) : ''),
    [node],
  );

  if (!node) {
    return (
      <VStack
        flex="1"
        justifyContent="center"
        alignItems="center"
        px={4}
        py={8}
      >
        <MetaDefault color="fg.muted" textAlign="center">
          Select a node in the trace to inspect its inputs and outputs.
        </MetaDefault>
      </VStack>
    );
  }

  const hasInput = node.input !== undefined;
  const hasOutput = node.output !== undefined;

  return (
    <VStack flex="1" alignItems="stretch" gap={3} minH={0} overflow="hidden">
      <HStack
        gap={2}
        alignItems="center"
        flexWrap="wrap"
        px={4}
        pt={3}
        pb={2}
        borderBottomWidth="1px"
        borderColor="border.subtle"
      >
        <Badge variant="emphasis" size="sm">
          {KIND_LABEL[node.kind]}
        </Badge>
        <MonoSmall color="fg.default" fontWeight="semibold">
          {node.name}
        </MonoSmall>
        <StatusBadge status={node.status} />
      </HStack>

      <Box flex="1" minH={0} overflow="hidden" px={4} pb={4}>
        <Tabs.Root
          defaultValue="inputs"
          variant="outline"
          h="full"
          display="flex"
          flexDirection="column"
        >
          <Tabs.List
            gap={0}
            borderBottomWidth="1px"
            borderColor="border.subtle"
            mb={3}
          >
            <Tabs.Trigger {...tabTriggerProps('inputs')}>Inputs</Tabs.Trigger>
            <Tabs.Trigger {...tabTriggerProps('outputs')}>Outputs</Tabs.Trigger>
            <Tabs.Trigger {...tabTriggerProps('metadata')}>
              Metadata
            </Tabs.Trigger>
            <Tabs.Indicator />
          </Tabs.List>

          <Box flex="1" minH={0} overflow="auto">
            <Tabs.Content value="inputs" mt={0}>
              {hasInput ? (
                <IoContent io={node.input} />
              ) : (
                <EmptyState label="No inputs for this node" />
              )}
            </Tabs.Content>
            <Tabs.Content value="outputs" mt={0}>
              {hasOutput ? (
                <IoContent io={node.output} />
              ) : (
                <EmptyState label="No outputs for this node" />
              )}
            </Tabs.Content>
            <Tabs.Content value="metadata" mt={0}>
              <VStack alignItems="stretch" gap={3}>
                <Box borderRadius="md" overflow="hidden">
                  <ReadOnlyCodeEditor
                    value={metadataJson || '{}'}
                    language="json"
                    showLineNumbers
                    maxHeight="220px"
                  />
                </Box>
                <VStack alignItems="stretch" gap={1.5}>
                  <MetaDefault
                    color="fg.muted"
                    fontWeight="semibold"
                    textTransform="uppercase"
                  >
                    Raw node
                  </MetaDefault>
                  <Box borderRadius="md" overflow="hidden">
                    <ReadOnlyCodeEditor
                      value={rawJson}
                      language="json"
                      showLineNumbers
                      maxHeight="320px"
                    />
                  </Box>
                </VStack>
              </VStack>
            </Tabs.Content>
          </Box>
        </Tabs.Root>
      </Box>
    </VStack>
  );
}
